import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  generatePath,
  RouteComponentProps,
  withRouter,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { Button } from "@material-ui/core";
import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";
import { SNACKBAR_TYPES } from "components/Snackbar";
import { lengthValidator } from "utils/validators";
import TagDictionary from "stores/dictionary/TagDictionary";

import { PUBLISH_STATUSES } from "constants/models";
import ROUTES from "navigation/routes";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import {
  BackButtonWrapper,
  Column,
  Container,
  ControlContainer,
  ControlsContainer,
  GroupRow,
  PublishStatusWrapper,
  Row,
} from "./styles";
import ContentPatternStore, {
  ActivityLevelDictionary,
  AgeRangeDictionary,
  GenderDictionary,
  WeightGoalTypeDictionary,
} from "stores/PatternStore";
import ClaimDictionary from "stores/dictionary/ClaimDictionary";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ patternId: string }, {}, LocationState> &
  SnackbarContextProps & {};

@observer
class PatternDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewPattern() {
    const { patternId } = this.props.match.params;

    return patternId === undefined;
  }

  get patternId() {
    const { patternId } = this.props.match.params;

    const parsedPatternId = Number.parseInt(patternId);
    return typeof parsedPatternId === "number" ? parsedPatternId : undefined;
  }

  get backClickInfo() {
    return " Back to Patterns";
  }

  get backClickPath() {
    return ROUTES.CONTENT_PATTERNS_PATTERNS;
  }

  @computed get displayValues(): any {
    const formData = ContentPatternStore.details?.formData;

    const gender = this.genderOptions.find(
      obj => obj.value === formData?.sexId,
    );
    const ageRange = this.ageRangeOptions.find(
      obj => obj.value === formData?.ageRangeId,
    );
    const activityLevel = this.activityLevelOptions.find(
      obj => obj.value === formData?.activityLevelId,
    );
    const weightGoalType = this.weightGoalTypeOptions.find(
      obj => obj.value === formData?.weightGoalTypeId,
    );

    return {
      ...formData,
      genderLabel: gender?.label,
      ageRangeLabel: ageRange?.label,
      activityLevelLabel: activityLevel?.label,
      weightGoalTypeLabel: weightGoalType?.label,
    };
  }

  @computed get claimOptions() {
    return [...ClaimDictionary.options];
  }

  @computed get tagOptions() {
    return [...TagDictionary.options];
  }

  @computed get initialValues() {
    if (this.isNewPattern) {
      return {};
    } else {
      return {
        ...ContentPatternStore.details?.formData,
      };
    }
  }

  @computed get patternPublishStatus() {
    return ContentPatternStore.details?.publishStatus;
  }

  componentDidMount() {
    const { history } = this.props;

    ClaimDictionary.fetch();
    if (this.isNewPattern) {
      history.replace({ state: { editMode: true } });
    } else {
      ContentPatternStore.fetchPatternDetails(this.patternId);
      Promise.all([
        AgeRangeDictionary.fetch(),
        WeightGoalTypeDictionary.fetch(),
        GenderDictionary.fetch(),
        ActivityLevelDictionary.fetch(),
        TagDictionary.fetch(),
      ]);
    }
  }

  componentWillUnmount() {
    ContentPatternStore.resetDetails();
  }

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewPattern) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS, {
      patternId: this.patternId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS, {
      patternId: this.patternId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  @computed get genderOptions() {
    return [...GenderDictionary.options];
  }

  @computed get activityLevelOptions() {
    return [...ActivityLevelDictionary.options];
  }

  @computed get ageRangeOptions() {
    const options = [...AgeRangeDictionary.options];
    return options;
  }

  @computed get weightGoalTypeOptions() {
    return [...WeightGoalTypeDictionary.options];
  }

  onSubmit = async (values: any) => {
    const shouldShowUpdateConfirmation =
      this.patternPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.patternPublishStatus);

    const submitPattern = shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation
      : this.submit;

    submitPattern(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const { id, title, claims } = values;
    console.log(this.patternId);

    const data = {
      title: title?.trim(),
    };

    try {
      if (this.isNewPattern) {
        const payload = { ...data };
        const id = await ContentPatternStore.createPattern(payload);

        if (id) {
          const path = generatePath(
            ROUTES.CONTENT_PATTERNS_PATTERNS_LINK_DETAILS,
            {
              patternId: id,
            },
          );
          history.push({ pathname: path, state: { editMode: false } });
          ContentPatternStore.fetchPatternDetails(id);
        }
      } else {
        const payload = { ...data, id: this.patternId };
        const response = await ContentPatternStore.editPatternInfo(payload);
        const dataDetail = {
          patternID: response.id,
          sexID: values.sexId,
          activityLevelID: values.activityLevelId,
          ageRangeID: values.ageRangeId,
          weightGoalTypeID: values.weightGoalTypeId,
          tagIds: values.tags.map((tag: any) => tag.id),
          claimIds: claims?.map(({ id }: any) => id),
        };
        console.log(dataDetail);
        await ContentPatternStore.editPatternDetailInfo(dataDetail);

        await ContentPatternStore.fetchPatternDetails(id);
        await ContentPatternStore.fetchDetailOptions(id);
      }
      this.exitEditMode();
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  submitPatternDetail = async (values: any) => {
    const { setMessage } = this.props;
    const {
      patternId,
      sexId,
      activityLevelId,
      ageRangeId,
      weightGoalTypeId,
    } = values;

    const data = {
      patternID: patternId,
      sexID: sexId,
      activityLevelID: activityLevelId,
      ageRangeID: ageRangeId,
      weightGoalTypeID: weightGoalTypeId,
    };

    try {
      await ContentPatternStore.editPatternDetailInfo(data);
      this.exitEditMode();
      ContentPatternStore.fetchPatternDetails(data.patternID);
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Pattern Details"
        leftControls={
          this.patternPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.patternPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickInfo}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={ContentPatternStore.isPatternLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewPattern}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{
              dirty: true,
              dirtyFields: true,
              initialValues: true,
              values: true,
            }}
            onChange={debounce(this.onChangeIsPopupDirty, 500)}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode || this.isNewPattern ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>
            {!this.isNewPattern ? (
              <>
                <GroupRow>
                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <TextFieldControl
                          variant="outlined"
                          margin="none"
                          name="title"
                          label="Title"
                          validate={lengthValidator(64)}
                          fullWidth
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Title"
                          value={this.displayValues?.title}
                        />
                      )}
                    </Row>
                  </Column>
                </GroupRow>
                <GroupRow>
                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <DropdownFieldControl
                          name="sexId"
                          options={this.genderOptions}
                          label="Gender"
                          multi={false}
                          isClearable
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Gender"
                          value={this.displayValues?.genderLabel}
                        />
                      )}
                    </Row>
                  </Column>

                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <DropdownFieldControl
                          name="activityLevelId"
                          options={this.activityLevelOptions}
                          label="Activity Level"
                          multi={false}
                          isClearable
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Activity Level"
                          value={this.displayValues?.activityLevelLabel}
                        />
                      )}
                    </Row>
                  </Column>
                </GroupRow>

                <GroupRow>
                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <DropdownFieldControl
                          name="ageRangeId"
                          options={this.ageRangeOptions}
                          label="Age Range"
                          multi={false}
                          isClearable
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Age Range"
                          value={this.displayValues?.ageRangeLabel}
                        />
                      )}
                    </Row>
                  </Column>

                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <DropdownFieldControl
                          name="weightGoalTypeId"
                          options={this.weightGoalTypeOptions}
                          label="Weight Goal Type"
                          multi={false}
                          isClearable
                          required
                        />
                      ) : (
                        <SimpleField
                          label="Weight Goal Type"
                          value={this.displayValues?.weightGoalTypeLabel}
                        />
                      )}
                    </Row>
                  </Column>
                </GroupRow>

                <Row>
                  {this.isEditMode ? (
                    <DropdownFieldControl
                      name="tags"
                      options={this.tagOptions}
                      label="Tags"
                      multi
                      isClearable
                    />
                  ) : (
                    <SimpleField
                      label="Tags"
                      value={this.displayValues?.tags}
                    />
                  )}
                </Row>
                <Row>
                  {this.isEditMode ? (
                    <DropdownFieldControl
                      name="claims"
                      options={this.claimOptions}
                      label="Claims"
                      multi
                      isClearable
                    />
                  ) : (
                    <SimpleField
                      label="Claims"
                      value={this.displayValues?.claims}
                    />
                  )}
                </Row>
              </>
            ) : (
              <GroupRow>
                <Column>
                  <Row>
                    <TextFieldControl
                      variant="outlined"
                      margin="none"
                      name="title"
                      label="Title"
                      validate={lengthValidator(64)}
                      fullWidth
                      required
                    />
                  </Row>
                </Column>
              </GroupRow>
            )}
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ContentPatternStore.isPatternManaging}
      fullWidth
    >
      {this.isNewPattern ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(PatternDetails));
